/**
 * workspaceStore — workspaces remotos do usuário autenticado.
 *
 * Gerencia: lista de workspaces (via memberships), workspace ativo,
 * membros, assinatura, plano e role do usuário no workspace atual.
 *
 * Persistência leve: só `lastWorkspaceSlug` vai para localStorage
 * (chave morpherd_last_workspace_slug).
 */

import { create } from 'zustand'
import { workspaceApi } from '@/api/workspaceApi'
import { memberApi } from '@/api/memberApi'
import { invitationApi } from '@/api/invitationApi'
import { subscriptionApi } from '@/api/subscriptionApi'
import { ApiError } from '@/api/httpClient'
import { useAuthStore } from '@/stores/authStore'
import type {
  ChangePlanRequest,
  ChangePlanResult,
  CreateWorkspaceRequest,
  CreateInvitationRequest,
  Membership,
  Subscription,
  UpdateMemberRequest,
  Workspace,
  WorkspaceInvitation,
  WorkspaceMember,
  WorkspaceRole,
} from '@/api/types'

const LAST_WORKSPACE_KEY = 'morpherd_last_workspace_slug'

interface WorkspaceStore {
  workspaces: Workspace[]
  currentWorkspace: Workspace | null
  members: WorkspaceMember[]
  subscription: Subscription | null
  myRole: WorkspaceRole | null

  loadingWorkspaces: boolean
  loadingMembers: boolean
  loadingSubscription: boolean

  /** Incrementado sempre que a lista de workspaces deve ser recarregada externamente. */
  workspaceListVersion: number
  bumpWorkspaceListVersion: () => void

  /** Carrega a lista de workspaces a partir das memberships do /me e restaura o último ativo. */
  loadWorkspaces: (memberships: Membership[]) => Promise<void>

  /**
   * Busca um workspace pelo slug.
   * Se já estiver na lista local usa o cache; caso contrário faz GET by slug.
   * Define currentWorkspace e persiste o slug no localStorage.
   */
  loadWorkspaceBySlug: (slug: string) => Promise<Workspace>

  /** Seleciona um workspace por slug e carrega membros + subscription. */
  selectWorkspace: (slug: string) => Promise<void>

  /**
   * Seleciona um workspace por ID numérico (uso interno — useLoadProject).
   * Não faz chamada de rede; apenas encontra o workspace na lista local.
   */
  selectWorkspaceById: (id: number) => Promise<void>

  createWorkspace: (body: CreateWorkspaceRequest) => Promise<Workspace>
  loadMembers: (workspaceId?: number) => Promise<void>
  inviteMember: (body: CreateInvitationRequest) => Promise<WorkspaceInvitation>
  updateMember: (memberId: number, body: UpdateMemberRequest) => Promise<WorkspaceMember>
  removeMember: (memberId: number) => Promise<void>
  leaveCurrentWorkspace: () => Promise<void>
  deleteCurrentWorkspace: () => Promise<void>
  loadSubscription: (workspaceId?: number) => Promise<void>
  changePlan: (body: ChangePlanRequest) => Promise<ChangePlanResult>
  revertDowngrade: () => Promise<Subscription>
  cancelSubscription: () => Promise<Subscription>
  clearCurrentWorkspace: () => void
  reset: () => void
}

const initial = {
  workspaces: [],
  currentWorkspace: null,
  members: [],
  subscription: null,
  myRole: null,
  loadingWorkspaces: false,
  loadingMembers: false,
  loadingSubscription: false,
  workspaceListVersion: 0,
}

export const useWorkspaceStore = create<WorkspaceStore>((set, get) => ({
  ...initial,

  async loadWorkspaces(memberships) {
    if (!memberships.length) {
      set({ workspaces: [], currentWorkspace: null, myRole: null })
      return
    }
    set({ loadingWorkspaces: true })
    try {
      const page = await workspaceApi.list({ limit: 100 })
      const list = page.results
      const stored =
        typeof window !== 'undefined' ? window.localStorage.getItem(LAST_WORKSPACE_KEY) : null
      const knownSlugs = new Set(list.map((w) => w.slug))
      const lastSlug = stored && knownSlugs.has(stored) ? stored : (list[0]?.slug ?? null)

      const current = list.find((w) => w.slug === lastSlug) ?? list[0] ?? null
      const role = memberships.find((m) => m.workspace_id === current?.id)?.role ?? null
      set({ workspaces: list, currentWorkspace: current, myRole: role, loadingWorkspaces: false })

      if (current) persist(current.slug)
    } catch {
      set({ loadingWorkspaces: false })
    }
  },

  async loadWorkspaceBySlug(slug) {
    // Cache hit — already in the list
    const cached = get().workspaces.find((w) => w.slug === slug)
    if (cached) {
      const role = getMembershipRole(cached.id) ?? cached.my_role ?? null
      set({ currentWorkspace: cached, myRole: role })
      persist(cached.slug)
      return cached
    }

    // Fetch from API
    try {
      const ws = await workspaceApi.getBySlug(slug)
      set((s) => ({
        workspaces: s.workspaces.some((w) => w.id === ws.id)
          ? s.workspaces.map((item) => (item.id === ws.id ? ws : item))
          : [...s.workspaces, ws],
        currentWorkspace: ws,
        myRole: getMembershipRole(ws.id) ?? ws.my_role ?? null,
      }))
      persist(ws.slug)
      return ws
    } catch (err) {
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        clearPersistedSlug()
        set({ currentWorkspace: null, members: [], subscription: null, myRole: null })
      }
      throw err
    }
  },

  async selectWorkspace(slug) {
    const ws = await get().loadWorkspaceBySlug(slug)
    await Promise.all([get().loadMembers(ws.id), get().loadSubscription(ws.id)])
  },

  async selectWorkspaceById(id) {
    // Internal helper — used by useLoadProject to keep currentWorkspace in sync
    // without knowing the slug ahead of time.
    const ws = get().workspaces.find((w) => w.id === id)
    if (!ws) return
    set({ currentWorkspace: ws, myRole: getMembershipRole(id) ?? ws.my_role ?? null })
    persist(ws.slug)
    await Promise.all([get().loadMembers(id), get().loadSubscription(id)]).catch(() => {
      /* best-effort */
    })
  },

  async createWorkspace(body) {
    const ws = await workspaceApi.create(body, { silent: true })
    set((s) => ({
      workspaces: [...s.workspaces, ws],
      currentWorkspace: ws,
      myRole: 1, // OWNER
    }))
    persist(ws.slug)
    return ws
  },

  async loadMembers(workspaceId) {
    const id = workspaceId ?? get().currentWorkspace?.id
    if (!id) return
    set({ loadingMembers: true })
    try {
      const page = await memberApi.list(id, { limit: 10 })
      set({ members: page.results, loadingMembers: false })
    } catch {
      set({ loadingMembers: false })
    }
  },

  async inviteMember(body) {
    const id = get().currentWorkspace?.id
    if (!id) throw new Error('No current workspace')
    const invitation = await invitationApi.create(id, body, { silent: true })
    return invitation
  },

  async updateMember(memberId, body) {
    const id = get().currentWorkspace?.id
    if (!id) throw new Error('No current workspace')
    const updated = await memberApi.patch(id, memberId, body, { silent: true })
    set((s) => ({
      members: s.members.map((m) => (m.id === memberId ? updated : m)),
    }))
    return updated
  },

  async removeMember(memberId) {
    const id = get().currentWorkspace?.id
    if (!id) throw new Error('No current workspace')
    await memberApi.remove(id, memberId, { silent: true })
    set((s) => ({ members: s.members.filter((m) => m.id !== memberId) }))
  },

  async leaveCurrentWorkspace() {
    const ws = get().currentWorkspace
    if (!ws) throw new Error('No current workspace')
    await workspaceApi.leave(ws.id, { silent: true })
    useAuthStore.setState((state) => ({
      memberships: state.memberships.filter((membership) => membership.workspace_id !== ws.id),
    }))
    set((s) => ({ workspaces: s.workspaces.filter((w) => w.id !== ws.id) }))
    get().clearCurrentWorkspace()
  },

  async deleteCurrentWorkspace() {
    const ws = get().currentWorkspace
    if (!ws) throw new Error('No current workspace')
    await workspaceApi.delete(ws.id)
    useAuthStore.setState((state) => ({
      memberships: state.memberships.filter((membership) => membership.workspace_id !== ws.id),
    }))
    set((s) => ({ workspaces: s.workspaces.filter((w) => w.id !== ws.id) }))
    get().clearCurrentWorkspace()
  },

  async loadSubscription(workspaceId) {
    const id = workspaceId ?? get().currentWorkspace?.id
    if (!id) return
    set({ loadingSubscription: true })
    try {
      const subscription = await subscriptionApi.get(id)
      set({ subscription, loadingSubscription: false })
    } catch {
      set({ loadingSubscription: false })
    }
  },

  async changePlan(body) {
    const id = get().currentWorkspace?.id
    if (!id) throw new Error('No current workspace')
    const result = await subscriptionApi.changePlan(id, body)
    set({ subscription: result.subscription })
    return result
  },

  async revertDowngrade() {
    const id = get().currentWorkspace?.id
    if (!id) throw new Error('No current workspace')
    const subscription = await subscriptionApi.revertDowngrade(id)
    set({ subscription })
    return subscription
  },

  async cancelSubscription() {
    const id = get().currentWorkspace?.id
    if (!id) throw new Error('No current workspace')
    const subscription = await subscriptionApi.cancel(id)
    set({ subscription })
    return subscription
  },

  clearCurrentWorkspace() {
    clearPersistedSlug()
    set({ currentWorkspace: null, members: [], subscription: null, myRole: null })
  },

  bumpWorkspaceListVersion() {
    set((s) => ({ workspaceListVersion: s.workspaceListVersion + 1 }))
  },

  reset() {
    clearPersistedSlug()
    set(initial)
  },
}))

function persist(slug: string) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LAST_WORKSPACE_KEY, slug)
  }
}

function getMembershipRole(workspaceId: number) {
  return (
    useAuthStore
      .getState()
      .memberships.find((membership) => membership.workspace_id === workspaceId)?.role ?? null
  )
}

export function clearPersistedSlug() {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(LAST_WORKSPACE_KEY)
  }
}
